import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotate,
  faUpRightFromSquare,
  faExpand,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';

import { http } from '../../features/jobs/api/http';
import { API } from '../../config/api';
import { prepareHmiPage, hmiPagePath } from '../../helpers/hmiEmbed';

// The three Pi machines serve their own panel from their own box; everything
// else is served by machine_hmi_interface on the main host. Only the "open it
// on :5005" escape hatch needs this — the embedded panel is always read from
// the main host's copy.
const HMI_HOST = {
  l70: '192.168.1.172',
  dhc600: '192.168.1.238',
  ath600: '192.168.1.179'
};
const hmiDirectUrl = machineId => {
  const m = String(machineId || '').toLowerCase();
  return `http://${HMI_HOST[m] || '192.168.1.162'}:5005/${m}-hmi`;
};

// The operator panel, inside the app.
//
// Not a port and not a copy: the page served here is the same file the Siemens
// terminal on the machine is running, fetched as text and dropped into a
// srcdoc iframe, which inherits this origin — so the token in localStorage is
// already the panel's token and nothing needs CORS.
//
// What that buys is the thing the button could not do: the old one opened
// `http://192.168.1.162:5005/l75-hmi`, which exists only on the factory
// network. Through the tunnel it was a dead link over HTTPS before the request
// was even made.

// The panels are built for an industrial terminal: a fixed, wide layout meant
// to be the only thing on a screen. Giving the frame a real height and letting
// it scroll internally is closer to the panel than squeezing it into a card.
const FRAME_MIN_HEIGHT = 900;

const MachineHmiPanel = () => {
  const { machineId = '' } = useParams();
  const mid = String(machineId).toLowerCase();
  const navigate = useNavigate();

  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [reloadAt, setReloadAt] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    http
      .get(hmiPagePath(mid), { responseType: 'text', timeout: 20000 })
      .then(({ data }) => {
        if (!alive) return;
        setRaw(typeof data === 'string' ? data : String(data ?? ''));
      })
      .catch(e => {
        if (!alive) return;
        const status = e?.response?.status;
        setErr(
          status === 404
            ? `There is no operator panel for ${mid.toUpperCase()}.`
            : e?.response?.data?.error || e?.message || 'Could not load the panel.'
        );
        setRaw('');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [mid, reloadAt]);

  // Rewritten for THIS browser: per-port on the LAN, same-origin through the
  // tunnel. The panel's own constants are hardcoded to 192.168.1.162 — the
  // same problem config/api.js solved for the app.
  const prepared = useMemo(
    () => prepareHmiPage(raw, { redis: API.REDIS, backend: API.BACKEND }),
    [raw]
  );

  const fullscreen = () => {
    const el = frameRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
  };

  return (
    <Card className="mb-9 mt-3">
      <Card.Body className="pb-0">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="mb-1">
              Operator panel — {mid.toUpperCase()}
              <Badge bg="info" className="ms-2 fs-10 align-middle">
                live
              </Badge>
            </h3>
            <div className="fs-9 text-700" style={{ maxWidth: 720 }}>
              The same panel the terminal on the machine is running, not a copy of
              it. Everything it can do there it can do here — start a drum, change
              the shift, assign raw material — and unlike the old button it works
              from outside the factory network.
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setReloadAt(Date.now())}
              title="Fetch the panel again"
            >
              <FontAwesomeIcon icon={faRotate} className="me-1" />
              Reload
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={fullscreen}>
              <FontAwesomeIcon icon={faExpand} className="me-1" />
              Fullscreen
            </Button>
            <Button
              size="sm"
              variant="outline-info"
              onClick={() => window.open(hmiDirectUrl(mid), '_blank', 'noopener')}
              title="The panel on its own server — factory network only"
            >
              <FontAwesomeIcon icon={faUpRightFromSquare} className="me-1" />
              Open on :5005
            </Button>
            <Button
              as={Link}
              to={`/apps/machine-management/machine-queue/${mid}/edit`}
              size="sm"
              variant="phoenix-secondary"
            >
              Back to Queue Edit
            </Button>
          </div>
        </div>

        {err && (
          <Alert variant="danger" className="py-2 fs-9">
            {err}
            <Button variant="link" className="p-0 ms-2 fs-9" onClick={() => navigate(-1)}>
              go back
            </Button>
          </Alert>
        )}

        {/* A panel edited on the floor could drop or rename a constant, and it
            would then quietly go on pointing at 192.168.1.162 — working on the
            LAN and failing everywhere else, which is the failure this page
            exists to remove. Said out loud rather than left to be discovered. */}
        {!loading && !err && prepared.missing.length > 0 && (
          <Alert variant="warning" className="py-2 fs-9">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
            {prepared.missing.join(', ')} could not be rewritten in this panel — those
            calls will still aim at the factory address and will fail from outside.
          </Alert>
        )}
      </Card.Body>

      <div className="px-3 pb-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="fs-9 text-700 mt-2">Loading the panel…</div>
          </div>
        ) : prepared.html ? (
          <iframe
            ref={frameRef}
            // srcDoc, not src: it inherits this origin, so localStorage — and
            // with it the token — is already shared with the app.
            srcDoc={prepared.html}
            title={`${mid} operator panel`}
            style={{
              width: '100%',
              height: `${FRAME_MIN_HEIGHT}px`,
              border: '1px solid var(--phoenix-border-color)',
              borderRadius: '0.5rem',
              background: '#fff'
            }}
          />
        ) : null}
      </div>
    </Card>
  );
};

export default MachineHmiPanel;
